import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import algoliasearchHelperFn from 'algoliasearch-helper';
import { AlertCircle, FileText, Loader, Search } from 'lucide-react';
import {
  createKbAlgoliaSearchHelper,
  isAlgoliaSearchConfigured,
  type KbAlgoliaHit,
} from '../lib/algoliaSearch';

export function KbAlgoliaSearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [hits, setHits] = useState<KbAlgoliaHit[]>([]);
  const [nbHits, setNbHits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const configured = isAlgoliaSearchConfigured();

  const helper = useMemo(
    () => (configured ? createKbAlgoliaSearchHelper({ hitsPerPage: 25 }) : null),
    [configured],
  );

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!helper) {
      return;
    }

    const onResult = ({
      results,
    }: {
      results: algoliasearchHelperFn.SearchResults;
    }) => {
      setHits((results.hits ?? []) as KbAlgoliaHit[]);
      setNbHits(results.nbHits ?? 0);
      setError(null);
      setLoading(false);
    };

    const onError = ({ error: err }: { error: Error }) => {
      setHits([]);
      setNbHits(0);
      setError(err.message || 'Search failed');
      setLoading(false);
    };

    helper.on('result', onResult);
    helper.on('error', onError);

    return () => {
      helper.removeListener('result', onResult);
      helper.removeListener('error', onError);
    };
  }, [helper]);

  useEffect(() => {
    if (!helper) {
      return;
    }
    if (debounced.length === 0) {
      setHits([]);
      setNbHits(0);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    helper.setQuery(debounced).search();
  }, [debounced, helper]);

  const openHit = (hit: KbAlgoliaHit) => {
    navigate(`/documents/${hit.objectID}`);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center gap-3">
          <Search className="h-8 w-8 shrink-0 text-slate-700 dark:text-slate-300" aria-hidden />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 sm:text-3xl">
              Search knowledge base
            </h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Full-text search against the Algolia index (titles, tags, state, jurisdiction).
            </p>
          </div>
        </div>

        {!configured ? (
          <div
            className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100"
            role="status"
          >
            <AlertCircle className="h-5 w-5 shrink-0" aria-hidden />
            <div className="min-w-0 text-sm leading-relaxed">
              <p className="font-medium">Algolia search is not configured</p>
              <p className="mt-1 text-amber-800 dark:text-amber-200/90">
                Add a{' '}
                <strong className="font-semibold">search-only</strong> API key to{' '}
                <code className="rounded bg-amber-100 px-1 py-0.5 text-xs dark:bg-amber-900/80">
                  VITE_ALGOLIA_SEARCH_KEY
                </code>{' '}
                in your Vite env. Optional overrides:{' '}
                <code className="rounded bg-amber-100 px-1 py-0.5 text-xs dark:bg-amber-900/80">
                  VITE_ALGOLIA_APP_ID
                </code>
                ,{' '}
                <code className="rounded bg-amber-100 px-1 py-0.5 text-xs dark:bg-amber-900/80">
                  VITE_ALGOLIA_INDEX_NAME
                </code>
                .
              </p>
            </div>
          </div>
        ) : (
          <>
            <label className="block">
              <span className="sr-only">Search</span>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                  aria-hidden
                />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search documents…"
                  autoComplete="off"
                  className="w-full rounded-lg border border-slate-200 bg-white py-3 pl-11 pr-10 text-slate-900 shadow-sm outline-none ring-slate-800 placeholder:text-slate-400 focus:border-slate-300 focus:ring-2 focus:ring-slate-800/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-slate-200/20"
                />
                {loading ? (
                  <Loader
                    className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-slate-400"
                    aria-label="Loading"
                  />
                ) : null}
              </div>
            </label>

            {error ? (
              <div
                className="mt-4 flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
                role="alert"
              >
                <AlertCircle className="h-5 w-5 shrink-0" aria-hidden />
                <p>{error}</p>
              </div>
            ) : null}

            {debounced.length > 0 && !loading && !error ? (
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                {nbHits === 0 ? 'No results.' : `${nbHits} result${nbHits === 1 ? '' : 's'}`}
              </p>
            ) : null}

            {debounced.length === 0 && !error ? (
              <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
                Type a query to search the index.
              </p>
            ) : null}

            <ul className="mt-4 space-y-2">
              {hits.map((hit) => (
                <li key={hit.objectID}>
                  <button
                    type="button"
                    onClick={() => openHit(hit)}
                    className="flex w-full items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600 dark:hover:bg-slate-800/80"
                  >
                    <FileText className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {hit.title?.trim() || 'Untitled'}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                        {hit.state ? <span>State: {hit.state}</span> : null}
                        {hit.jurisdiction ? <span>Jurisdiction: {hit.jurisdiction}</span> : null}
                        {hit.tags && hit.tags.length > 0 ? (
                          <span className="truncate">Tags: {hit.tags.join(', ')}</span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
